import {
  Component,
  Inject,
  ViewChild,
  ElementRef,
  OnInit,
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TAB } from '@angular/cdk/keycodes';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { debounceTime, switchMap } from 'rxjs/operators';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { Dictionary } from 'app/shared/models';
import { RelatedToType } from 'app/shared/enums';
import { AccountState } from 'app/state/account/state';
import { Account } from 'app/core/models/account.types';

@Component({
  selector: 'tags-modal',
  templateUrl: './tags-modal.component.html',
  styleUrls: ['./tags-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsModalComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  public tags: string[] = [];
  public foundTags: Dictionary<number>;
  public selectable = true;
  public removable = true;
  public separatorKeysCodes: number[] = [TAB];
  public tagForm: UntypedFormGroup;
  private account: Account;

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    private readonly matDialogRef: MatDialogRef<TagsModalComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly store: Store,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngAfterViewInit(): void {
    this.tagsValueListener();
  }

  private buildForm(): void {
    this.tagForm = this.formBuilder.group({
      tag_list: [[]],
    });

    this.tags = this.data.tags;

    this.tagForm.get('tag_list').setValue(this.tags);
  }

  private tagsValueListener(): void {
    this.tagForm
      .get('tag_list')
      .valueChanges.pipe(
        untilComponentDestroyed(this),
        debounceTime(400),
        switchMap((value: string) => {
          return this.accountService.getAccountTags(RelatedToType.Project, value).pipe(untilComponentDestroyed(this));
        }),
      )
      .subscribe((tags) => {
        this.foundTags = tags;
        this.cdRef.detectChanges();
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }

  save(): void {
    this.matDialogRef.close({ tags: this.tags });
  }

  addTag(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;

    if ((input.value || '').trim()) this.tags.push(input.value.trim());

    if (input) input.value = '';
  }

  removeTag(tag: string): void {
    this.tags.splice(
      this.tags.findIndex((item) => item === tag),
      1,
    );
  }

  selectedTag(event: MatAutocompleteSelectedEvent): void {
    this.tags.push(event.option.viewValue);
    this.tagInput.nativeElement.value = '';
    this.tagForm.get('tag_list').setValue(null);
  }
}
